import { useState, useEffect } from 'react';
import { supabase } from 'common/src/supabase';
import { Button, Input, Spinner } from '@nextui-org/react';
import { toast } from 'react-toastify';
import { useLocation } from 'wouter';
import useBrand from 'common/src/hooks/useBrand';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyingSession, setVerifyingSession] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [, setLocation] = useLocation();
  const { assets: brandAssets } = useBrand();

  useEffect(() => {
    const handleAuth = async () => {
      try {
        // Wait for Supabase to process tokens from URL hash (implicit flow)
        // detectSessionInUrl: true handles this automatically
        await new Promise(resolve => setTimeout(resolve, 500));

        // Verify session exists
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting session:', error);
          toast.error('Error al verificar la sesión. Por favor solicita un nuevo link de recuperación.');
          setHasValidSession(false);
          return;
        }

        if (!session) {
          toast.error('Sesión inválida o expirada. Por favor solicita un nuevo link de recuperación.');
          setHasValidSession(false);
          return;
        }

        setHasValidSession(true);
      } catch (error) {
        console.error('Error checking session:', error);
        toast.error('Error al verificar la sesión');
        setHasValidSession(false);
      } finally {
        setVerifyingSession(false);
      }
    };

    handleAuth();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success('Contraseña actualizada exitosamente');
      setTimeout(() => {
        setLocation('/');
      }, 1500);
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Mostrar loader mientras verifica la sesión
  if (verifyingSession) {
    return (
      <div className='flex h-screen w-screen items-center justify-center'>
        <div className='flex flex-col items-center gap-4'>
          <Spinner size='lg' />
          <p className='text-sm text-default-500'>Verificando sesión...</p>
        </div>
      </div>
    );
  }

  // Si no hay sesión válida, mostrar mensaje de error
  if (!hasValidSession) {
    return (
      <div className='relative flex h-screen w-screen'>
        {/* Brand Logo */}
        <div className='absolute left-2 top-5 left-4 lg:left-5'>
          <div className='flex items-center'>
            <img src={brandAssets.fullLogoLight} alt={brandAssets.name} className='h-8' />
          </div>
        </div>

        {/* Error message */}
        <div className='flex w-full items-center justify-center lg:w-1/2'>
          <div className='w-full max-w-md p-8 bg-background rounded-lg shadow-md mx-4'>
            <div className='flex flex-col items-center gap-4'>
              <div className='rounded-full bg-red-50 dark:bg-red-950 p-3'>
                <svg
                  className='h-12 w-12 text-red-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                  />
                </svg>
              </div>
              {/* design-ignore: pantalla de recuperar clave, fuera del layout de la app */}
              <h2 className='text-2xl font-semibold text-center'>Link inválido o expirado</h2>
              <p className='text-sm text-default-500 text-center'>
                El link de recuperación no es válido o ha expirado. Por favor solicita un nuevo link.
              </p>
              <a
                href='/forgot-password'
                className='w-full'
              >
                <Button
                  color='primary'
                  className='w-full mt-4'
                >
                  Solicitar nuevo link
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Right side */}
        <div
          className='relative hidden w-1/2 flex-col-reverse rounded-medium shadow-small lg:flex'
          style={{
            backgroundImage: 'url(/cherry.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      </div>
    );
  }

  return (
    <div className='relative flex h-screen w-screen'>
      {/* Brand Logo */}
      <div className='absolute left-2 top-5 left-4 lg:left-5'>
        <div className='flex items-center'>
          <img src={brandAssets.fullLogoLight} alt={brandAssets.name} className='h-8' />
        </div>
      </div>

      {/* Reset Password Form */}
      <div className='flex w-full items-center justify-center lg:w-1/2'>
        <div className='w-full max-w-md p-8 bg-background rounded-lg shadow-md mx-4'>
          {/* design-ignore: pantalla de recuperar clave, fuera del layout de la app */}
          <h2 className='text-2xl font-semibold mb-2'>Restablecer contraseña</h2>
          <p className='text-sm text-default-500 mb-6'>
            Ingresa tu nueva contraseña
          </p>
          <form onSubmit={handleResetPassword} className='flex flex-col gap-4'>
            <Input
              type='password'
              label='Nueva Contraseña'
              placeholder='••••••••'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isRequired
              description='Mínimo 6 caracteres'
            />
            <Input
              type='password'
              label='Confirmar Contraseña'
              placeholder='••••••••'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              isRequired
            />
            <Button
              type='submit'
              color='primary'
              className='w-full'
              isLoading={loading}
            >
              Restablecer contraseña
            </Button>
          </form>
        </div>
      </div>

      {/* Right side */}
      <div
        className='relative hidden w-1/2 flex-col-reverse rounded-medium shadow-small lg:flex'
        style={{
          backgroundImage: 'url(/cherry.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    </div>
  );
};

export default ResetPassword;
